import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    VStack,
    Box,
    Image,
    Text,
    Button,
    Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import TransferProofTemplate from './transfer-proof-template';
import {
    isImageFile,
    isPdfFile,
    isJsonTransferProof,
    parseTransferProof,
    getProofType
} from './proof-utils';

/**
 * Reusable Proof Modal Component
 * Displays different types of proof data: JSON transfer proof, images, PDFs, or other files
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {object} data - The data object containing proof information
 * @param {string} proofField - The field name containing the proof data (default: 'proof')
 * @param {string} title - Custom modal title (optional)
 * @returns {JSX.Element} - Proof modal component
 */
const ProofModal = ({
    isOpen,
    onClose,
    data,
    proofField = 'proof',
    title = null
}) => {
    if (!data) return null;

    const proofData = data[proofField] || data.transfer_proof;
    const proofType = getProofType(proofData);

    // Determine modal title and size based on proof type
    const getModalTitle = () => {
        if (title) return title;

        switch (proofType) {
            case 'json':
                return 'Bukti Transfer Elektronik';
            case 'image':
                return 'Bukti Transfer';
            case 'pdf':
                return 'Bukti Transfer (PDF)';
            case 'file':
                return 'Bukti Transfer';
            default:
                return 'Bukti Transaksi';
        }
    };

    const getModalSize = () => {
        return proofType === 'json' ? '4xl' : 'xl';
    };

    const renderProofContent = () => {
        switch (proofType) {
            case 'json':
                const parsedProof = parseTransferProof(proofData);
                return parsedProof ? (
                    <TransferProofTemplate proofData={parsedProof} />
                ) : (
                    <Box textAlign="center" py={8}>
                        <Text color="red.500">Data bukti transfer tidak valid</Text>
                    </Box>
                );

            case 'image':
                return (
                    <VStack spacing={4} align="stretch">
                        <Box
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                            bg="gray.50"
                            p={4}
                        >
                            <Image
                                src={proofData}
                                alt="Bukti Transfer"
                                maxW="100%"
                                maxH="500px"
                                objectFit="contain"
                                mx="auto"
                                fallback={
                                    <Box
                                        h="200px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        bg="gray.100"
                                        color="gray.500"
                                    >
                                        <Text>Tidak dapat memuat gambar</Text>
                                    </Box>
                                }
                            />
                        </Box>
                        <Button
                            as={Link}
                            href={proofData}
                            target="_blank"
                            rel="noopener noreferrer"
                            leftIcon={<ExternalLinkIcon />}
                            colorScheme="blue"
                            variant="outline"
                            size="sm"
                            _hover={{ textDecoration: 'none' }}
                        >
                            Buka di Tab Baru
                        </Button>
                    </VStack>
                );

            case 'pdf':
                return (
                    <VStack spacing={4} align="stretch">
                        <Box
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                            bg="gray.50"
                            p={4}
                        >
                            <Box
                                h="400px"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                bg="gray.100"
                                color="gray.600"
                                textAlign="center"
                            >
                                <Text fontSize="lg" fontWeight="semibold" mb={2}>
                                    📄 File PDF
                                </Text>
                                <Text fontSize="sm" mb={4}>
                                    File bukti transfer dalam format PDF.
                                    Klik tombol di bawah untuk membuka file.
                                </Text>
                            </Box>
                        </Box>
                        <Button
                            as={Link}
                            href={proofData}
                            target="_blank"
                            rel="noopener noreferrer"
                            leftIcon={<ExternalLinkIcon />}
                            colorScheme="blue"
                            variant="outline"
                            size="sm"
                            _hover={{ textDecoration: 'none' }}
                        >
                            Buka PDF di Tab Baru
                        </Button>
                    </VStack>
                );

            case 'file':
                return (
                    <VStack spacing={4} align="stretch">
                        <Box
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            overflow="hidden"
                            bg="gray.50"
                            p={4}
                        >
                            <Box
                                h="200px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bg="gray.100"
                                color="gray.500"
                                textAlign="center"
                            >
                                <VStack>
                                    <Text fontSize="lg" fontWeight="semibold">
                                        📎 File Bukti Transfer
                                    </Text>
                                    <Text fontSize="sm">
                                        Klik tombol di bawah untuk membuka file
                                    </Text>
                                </VStack>
                            </Box>
                        </Box>
                        <Button
                            as={Link}
                            href={proofData}
                            target="_blank"
                            rel="noopener noreferrer"
                            leftIcon={<ExternalLinkIcon />}
                            colorScheme="blue"
                            variant="outline"
                            size="sm"
                            _hover={{ textDecoration: 'none' }}
                        >
                            Buka di Tab Baru
                        </Button>
                    </VStack>
                );

            default:
                return (
                    <Box textAlign="center" py={8}>
                        <Text color="gray.500">
                            Format bukti tidak didukung atau data tidak tersedia
                        </Text>
                    </Box>
                );
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={getModalSize()} isCentered>
            <ModalOverlay />
            <ModalContent maxW={getModalSize() === '4xl' ? '4xl' : 'xl'}>
                <ModalHeader>{getModalTitle()}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {proofData ? renderProofContent() : (
                        <Box textAlign="center" py={8}>
                            <Text color="gray.500">Tidak ada bukti yang tersedia</Text>
                        </Box>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default ProofModal;